import {hasPendingKeywordSuggestions} from '../keyword-follow-up';
import { CommonModule, DatePipe } from '@angular/common';
import { ResponseTextComponent } from '../response-text.component';
import { VoiceInputStatusComponent } from '../voice-input-status.component';
import { ResponseActionsComponent } from '../response-actions.component';
import { ComposerControlsComponent, fitComposerInput } from '../composer-controls.component';
import { NativeResponseComponent } from '../native-response/native-response.component';
import { ResponseProgressComponent } from '../response-progress.component';
import { progressLabels, readResponseProgress } from '../response-progress';
import { nativeResponseViews, NativeReport, nativeReportType, novaPresentationEnabled } from '../native-response-view';
import { CHAT_ATTACHMENT_ACCEPT, isAllowedAttachmentMimeType, resolveAttachmentMimeType, SUPPORTED_ATTACHMENT_FORMATS } from '../attachment-formats';
import { shouldPasteAsText } from '../clipboard-text';
import { HttpEventType } from '@angular/common/http';
import { AfterViewInit, ChangeDetectorRef, Component, effect, ElementRef, EventEmitter, inject, input, Input, model, OnDestroy, OnInit, Output, untracked, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { marked } from 'marked';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { ProgressBarModule } from 'primeng/progressbar';
import { Skeleton } from "primeng/skeleton";
import { TextareaModule } from 'primeng/textarea';
import { TooltipModule } from 'primeng/tooltip';
import { lastValueFrom, Subscription, timeout } from 'rxjs';
import { ScribeService } from 'src/app/services/ai-speech-to-text.service';
import { Conversation, AiMessage, AiService, ConversationStreamEvent, GetMessagesResponse, ConversationTurnStatus, usesConversationExecution } from 'src/app/services/ai.service';
import { AuthService } from 'src/app/services/auth.service';
import { AiToolEventsService } from 'src/app/services/ai-tool-events.service';
import { ChatAttachmentService } from 'src/app/services/chat-attachment.service';
import { PosthogService } from 'src/app/services/posthog.service';
import { ToastV2Service } from 'src/app/services/toast-v2.service';
import { buildMarkers, parseMessageContent, stripPageContext } from '../attachment-markers';
import { stripCaptureMarker } from '../capture-marker';
import { MessageAttachmentsComponent } from './message-attachments/message-attachments.component';
import { ParseMessageContentPipe } from '../parse-message-content.pipe';
import { StripCaptureMarkerPipe } from '../strip-capture-marker.pipe';
import { ScanFromChatService } from '../scan-from-chat.service';
import { ScanFromChatDialogComponent } from '../scan-from-chat-dialog/scan-from-chat-dialog.component';
import { LatestWebsiteReportService } from '../latest-website-report.service';
import { ReportCandidate, ReportDiscoveryError, requestsLatestWebsiteReport } from '../latest-website-report';
import { appendWebsiteScanList, parseWebsiteScanList, requestsOtherWebsiteScans, requestsWebsiteScanList, websiteScanPath, WebsiteScanItem, WebsiteScanList } from '../website-scan-list';
import { appendWebsiteReportSummary, parseWebsiteReportSummary } from '../website-report-summary';
import { environment } from 'src/environments/environment';
import { WebsiteReviewComponent } from '../website-review/website-review.component';
import { appendWebsiteReportReference, parseWebsiteReportReference, historicalWebsiteReportId, reportReferencePath } from '../website-report-reference';
import { requestsWebsiteReportDiscussion } from '../nova-request-intent';
import { NovaWelcomeDeparture } from '../nova-orb-transition';
import {ConversationUpdatePoller, mergeConversationUpdates} from '../conversation-updates';
import {NovaActivityService} from '../../services/nova-activity.service';
import {conversationSendErrorMessage} from '../conversation-send-error';

type PendingAttachmentStatus = 'uploading' | 'ready' | 'error';

export interface ConversationLoadResult { conversationId: string; success: boolean }

interface PendingChatAttachment {
  localId: string;
  file: File;
  filename: string;
  mimeType: string;
  fileSize: number;
  status: PendingAttachmentStatus;
  progress: number;
  id?: string;
  previewUrl?: string;
  errorMessage?: string;
  uploadSubscription?: Subscription;
}

@Component({
  selector: 'app-messages-container',
  imports: [Skeleton, CommonModule, TextareaModule, FormsModule, ButtonModule, TooltipModule, DialogModule, ProgressBarModule, MessageAttachmentsComponent, ParseMessageContentPipe, StripCaptureMarkerPipe, ScanFromChatDialogComponent, WebsiteReviewComponent, NativeResponseComponent, ResponseProgressComponent, ResponseTextComponent, ResponseActionsComponent, ComposerControlsComponent, VoiceInputStatusComponent],
  providers: [DatePipe, ChatAttachmentService],
  templateUrl: './messages-container.component.html',
  styleUrl: './messages-container.component.scss'
})
export class MessagesContainerComponent implements OnInit, AfterViewInit, OnDestroy {
  private backgroundUpdates: AiMessage[] = [];
  private updatePoller = new ConversationUpdatePoller(
    () => {
      const chat = this.conversation();
      const userId = this.authService.getCurrentUser()?.uid;
      const organizationId = this.authService.getCurrentOrganization();
      return chat?.id && userId && organizationId && chat.attributes?.userId === userId &&
        chat.attributes?.organizationId === organizationId &&
        usesConversationExecution(chat) && !this.readOnly
        ? {conversationId: chat.id, userId, organizationId} : null;
    },
    () => !this.isStreaming && !this.activeConversationTurn && !this.isConversationLoading,
    (id, cursor) => lastValueFrom(this.aiService.getConversationUpdates(id, cursor).pipe(timeout(15000))),
    updates => {
      this.backgroundUpdates = mergeConversationUpdates(this.backgroundUpdates, updates);
      this.messages = mergeConversationUpdates(this.messages, this.backgroundUpdates);
      this.historyChangeDetector.markForCheck();
    },
  );
  @ViewChild('welcomeOrb') private welcomeOrb?: ElementRef<HTMLElement>;
  @ViewChild('welcomePanel') private welcomePanel?: ElementRef<HTMLElement>;
  @Output() welcomeDeparture = new EventEmitter<NovaWelcomeDeparture>();
  readonly resizeComposerInput = fitComposerInput;
  readonly nativeResponseViews = nativeResponseViews;
  messageKey(message:AiMessage){return message.presentationKey??=message.id;}
  responseRevealState(message:AiMessage){return message.revealState??={shown:0,content:''};}
  onTranscriptInteraction(event?:Event): void {
    if(event instanceof KeyboardEvent&&!['ArrowUp','ArrowDown','PageUp','PageDown','Home','End',' '].includes(event.key))return;
    this.messagesContainer?.nativeElement.classList.add('user-scrolled');
  }
  onTranscriptScroll():void{
    const viewport=this.messagesContainer?.nativeElement;
    if(!this.responseAnchor&&viewport&&viewport.scrollHeight-viewport.clientHeight-viewport.scrollTop<=4)viewport.classList.remove('user-scrolled');
  }
  hasVisibleResponse(message: AiMessage): boolean {
    // A search may defer an empty shell, but answer deltas are ready for display.
    return nativeResponseViews(message.views).length > 0 || !!message.content?.trim() || !message.isStreaming;
  }
  exploreReportSuggestion(value: { report: NativeReport; suggestion: string }): void {
    if (this.isAttachmentInputDisabled || this.messageInput.trim() || this.pendingAttachments.length) return;
    void this.sendMessageContent(`Help me explore this suggested next step for ${nativeReportType(value.report)} report "${value.report.name}" (report ID: ${value.report.id}):\n${value.suggestion.slice(0, 2000)}\nExplain what to check and why, using the available evidence. Do not change any findings or data.`);
  }
  discussNativeReport(report: NativeReport): void {
    if (this.isAttachmentInputDisabled || this.messageInput.trim() || this.pendingAttachments.length) return;
    void this.sendMessageContent(`Discuss ${nativeReportType(report)} report "${report.name}" (report ID: ${report.id}). Give the main findings and one useful next step.`);
  }
  get websiteReviewPresentationEnabled(): boolean {
    return novaPresentationEnabled(environment, usesConversationExecution(this.conversation?.()));
  }
  allSuggestions = input<string[]>([])
  active = input(true);
  @Output() conversationLoaded = new EventEmitter<ConversationLoadResult>();
  historyPendingReveal = false;
  historyArrived = false;
  historyLoadError = false;
  private historyFrame?: number;
  pendingInitialPrompt = input<string | null>(null)
  suggestions: string[] = []
  copyHandler?: (event: ClipboardEvent) => void;
  private readonly historyChangeDetector = inject(ChangeDetectorRef);
  toastService = inject(ToastV2Service)
  aiService = inject(AiService)
  latestWebsiteReportService = inject(LatestWebsiteReportService);
  isFindingWebsiteReport = false;
  reportDiscoveryError: string | null = null;
  readonly websiteScanPath = websiteScanPath;
  chatAttachmentService = inject(ChatAttachmentService)
  scanFromChatService = inject(ScanFromChatService)
  @ViewChild('messagesContainer') private messagesContainer!: ElementRef;
  datePipe = inject(DatePipe)
  conversation = model<Conversation | null>();
  messages: AiMessage[] = [];
  historyBefore: number | null = null;
  loadingEarlierMessages = false;
  private historyConversationId: string | null = null;
  messageInput: string = '';
  pendingAttachments: PendingChatAttachment[] = [];
  isDraggingFiles = false;
  private fileDragDepth = 0;
  isStreaming: boolean = false;
  activeConversationTurn: ConversationTurnStatus | null = null;
  private keywordFollowUpPoll?: ReturnType<typeof setTimeout>;
  private turnPoll?: ReturnType<typeof setTimeout>;
  private turnPollGeneration = 0;
  private onTurnComplete?: () => void;
  turnStatusLoading = false;
  streamingStatus!: string | null;
  private internalSelectConversationLoading = false
  @Input() isSelectConversationLoading = false
  @Output() getConversations = new EventEmitter()
  @Output() setConversation = new EventEmitter()
  skipNextEffect = false;
  @Input() assistant = false
  /**
   * Read-only mode for the super-admin "all conversations" view: hides the
   * composer, voice input, feedback controls and suggestions, and loads messages
   * through the super-admin endpoint instead of the owner-scoped one.
   */
  @Input() readOnly = false
  @Input() set initialMessages(messages: AiMessage[]) {
    this.messages = messages ?? [];
    this.trySendPendingInitialPrompt();
  }
  @Output() suggestionUsed = new EventEmitter()
  authService = inject(AuthService)
  aiToolEventsService = inject(AiToolEventsService)
  private readonly novaActivity = inject(NovaActivityService);
  private readonly activityHost = inject<ElementRef<HTMLElement>>(ElementRef);
  private releaseActivityView?: () => void;
  userId = this.authService.getCurrentUser()?.uid;
  @Input() attributes!: any;
  readonly scribe = inject(ScribeService);
  private readonly voiceOwner = {};
  voiceSendPending = false;

  get ownsVoiceInput(): boolean {return this.scribe?.isOwnedBy?.(this.voiceOwner) ?? false;}
  get voiceInProgress(): boolean {return this.ownsVoiceInput && this.scribe.isBusy();}
  get voiceTransitioning(): boolean {
    return this.voiceInProgress && this.scribe.status() !== 'recording';
  }

  async startVoiceInput(): Promise<void> {
    if (!this.active() || this.isAttachmentInputDisabled || this.messageInput.trim()) return;
    await this.scribe.startTranscription(this.voiceOwner);
  }

  async stopVoiceInput(): Promise<void> {
    const generation = this.turnPollGeneration;
    const conversationId = this.conversation()?.id;
    const text = await this.scribe.stopTranscription(this.voiceOwner);
    if (generation === this.turnPollGeneration && conversationId === this.conversation()?.id && this.ownsVoiceInput && text) {
      this.messageInput = text;
    }
  }
  private consumedPendingPromptKey: string | null = null;
  private loadedConversationId: string | null = null;

  readonly MAX_MESSAGE_LENGTH = 300_000;
  readonly MAX_ATTACHMENT_SIZE = 10 * 1024 * 1024;
  readonly MAX_ATTACHMENTS_PER_MESSAGE = 5;
  readonly CHAT_ATTACHMENT_ACCEPT = CHAT_ATTACHMENT_ACCEPT;
  readonly supportedAttachmentFormats = SUPPORTED_ATTACHMENT_FORMATS;
  private readonly GENERIC_FALLBACK_MESSAGE = 'Sorry, an error occurred. Please try again.';

  get isConversationLoading(): boolean {
    return this.internalSelectConversationLoading || this.isSelectConversationLoading;
  }

  get isReadOnlySharedConversation(): boolean {
    return this.userId !== this.conversation()?.attributes?.userId && !!this.conversation()?.id;
  }

  onScanRequested(attachment: { id: string; filename: string }): void {
    this.openScanDialog(attachment.id, attachment.filename);
  }

  onScanPendingAttachment(attachment: PendingChatAttachment): void {
    if (attachment.status !== 'ready' || !attachment.id || !this.canScanAttachment(attachment)) return;
    this.openScanDialog(attachment.id, attachment.filename);
  }

  canScanAttachment(attachment: PendingChatAttachment): boolean {
    return attachment.mimeType.startsWith('image/') || attachment.mimeType === 'application/pdf';
  }

  private openScanDialog(attachmentId: string, filename: string): void {
    this.scanFromChatService.open({
      attachmentId,
      filename,
      conversationId: this.conversation()?.id,
      conversationText: this.buildConversationText(),
    });
  }

  private buildConversationText(): string {
    return this.messages
      .map((message) => stripCaptureMarker(parseMessageContent(message.content).text))
      .filter((text) => !!text)
      .join('\n\n');
  }

  get isOverLimit(): boolean {
    return this.messageInput.length > this.MAX_MESSAGE_LENGTH;
  }

  get charsOverLimit(): number {
    return this.messageInput.length - this.MAX_MESSAGE_LENGTH;
  }

  get readyAttachments(): PendingChatAttachment[] {
    return this.pendingAttachments.filter((attachment) => attachment.status === 'ready' && !!attachment.id);
  }

  get activeAttachmentCount(): number {
    return this.pendingAttachments.filter((attachment) => attachment.status !== 'error').length;
  }

  get hasUploadingAttachments(): boolean {
    return this.pendingAttachments.some((attachment) => attachment.status === 'uploading');
  }

  get hasAttachmentErrors(): boolean {
    return this.pendingAttachments.some((attachment) => attachment.status === 'error');
  }

  get isResponseActive(): boolean {
    const last=this.messages.at(-1);
    return !!this.activeConversationTurn || this.isStreaming || !!last?.isStreaming || !!last?.isFinalizing || !!last?.isRevealing;
  }

  get isAttachmentInputDisabled(): boolean {
    return this.readOnly || this.isResponseActive || this.isConversationLoading || this.isReadOnlySharedConversation || this.voiceSendPending || this.voiceTransitioning;
  }

  get isSendDisabled(): boolean {
    return this.voiceSendPending || this.voiceTransitioning
      || (!this.messageInput.trim() && !this.readyAttachments.length)
      || !!this.activeConversationTurn
      || this.readOnly
      || this.isStreaming
      || !!this.messages.at(-1)?.isRevealing
      || this.isConversationLoading
      || this.isReadOnlySharedConversation
      || this.isOverLimit
      || this.hasUploadingAttachments
      || this.hasAttachmentErrors;
  }

  get sendTooltip(): string {
    if (this.isOverLimit) return 'Message is too long';
    if (this.hasUploadingAttachments) return 'Attachment upload is still in progress';
    if (this.hasAttachmentErrors) return 'Remove or retry failed attachments';
    if (!this.messageInput.trim() && !this.readyAttachments.length) return 'Message is empty';
    return '';
  }

  feedbackModalVisible = false;
  selectedMessageForFeedback: AiMessage | null = null;
  selectedFeedbackOption: string | null = null;
  feedbackObservation = '';
  feedbackOptions = [
    { value: 'missing_risks', label: 'Missed compliance risks' },
    { value: 'strict', label: "Too strict / over-flagged" },
    { value: 'wrong_risk', label: 'Risk level was wrong' },
    { value: 'rewrite_not_usable', label: 'Rewrite wasn’t usable' },
    { value: 'wrong_answer', label: 'Not what I asked for' },
    { value: 'not_supported', label: 'Not well supported' },
    { value: 'slow', label: 'Too slow' },
    { value: 'tone', label: 'Tone / style issue' },
    { value: 'privacy', label: 'Privacy or security concern' },
    { value: 'other', label: 'Other' },
  ];

  ngOnInit(): void {
    this.releaseActivityView = this.novaActivity.registerConversationView(() => {
      const host = this.activityHost.nativeElement;
      return !document.hidden && host.isConnected && host.getBoundingClientRect().height > 0 ? this.conversation()?.id || null : null;
    });
    this.suggestions = this.getRandomSuggestions(this.allSuggestions(), this.assistant ? 2 : 3)
    this.setupCopyHandler()
  }

  constructor(
    private posthogService: PosthogService,
  ) {
    effect(() => {
      const currentConversation = this.conversation();
      untracked(() => this.novaActivity.track(currentConversation));
      const currentConversationId = currentConversation?.id ?? null;

      if (this.skipNextEffect) {
        this.skipNextEffect = false;
        this.loadedConversationId = currentConversationId;
        return;
      }

      if (this.loadedConversationId !== currentConversationId) {
        this.resetConversation();
        this.loadedConversationId = currentConversationId;
      }

      if(!currentConversation) {
        this.resetConversation()
      } else {
        if (this.shouldAutoSendPendingPrompt()) {
          this.trySendPendingInitialPrompt();
          return;
        }
        this.selectConversation()
      }
    })

    effect(() => {
      this.allSuggestions()
      this.suggestions = this.getRandomSuggestions(this.allSuggestions(), this.assistant ? 2 : 3)
    })
    effect(() => {
      this.pendingInitialPrompt()
      this.trySendPendingInitialPrompt();
    })
    effect(() => {
      if (this.active()) untracked(() => this.presentLoadedHistory());
      else untracked(() => {if (this.voiceInProgress) void this.stopVoiceInput();});
    });
    effect(() => {
        if (this.ownsVoiceInput && this.scribe.fullTranscript()) {
          this.messageInput = this.scribe.fullTranscript();
        }
      })
    effect(() => {
      if (this.ownsVoiceInput && this.scribe.stoppedByTimeout()) {
        this.toastService.callInformationToast('You’ve reached the speech-to-text time limit.');
      }
    });
  }

  resetConversation() {
    // Cleanup must not subscribe the conversation effect to microphone ownership.
    // Otherwise pressing Start reruns the new-chat reset and cancels capture.
    untracked(() => this.scribe?.reset(this.voiceOwner));
    this.voiceSendPending = false;
    this.updatePoller?.stop();
    this.backgroundUpdates = [];
    this.responseAnchor = undefined;
    clearTimeout(this.keywordFollowUpPoll);
    clearTimeout(this.turnPoll);
    this.turnPollGeneration++;
    this.turnObserver?.disconnect();
    if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
    if (this.revealScrollFrame) cancelAnimationFrame(this.revealScrollFrame);
    if (this.historyFrame) cancelAnimationFrame(this.historyFrame);
    this.historyPendingReveal = false;
    this.historyArrived = false;
    this.historyLoadError = false;
    this.internalSelectConversationLoading = false;
    this.messagesContainer?.nativeElement.classList.remove('user-scrolled', 'settling');
    this.messagesContainer?.nativeElement.style.removeProperty('padding-bottom');
    this.activeConversationTurn = null;
    this.onTurnComplete = undefined;
    this.messages = [];
    this.historyBefore = null;
    this.loadingEarlierMessages = false;
    this.historyConversationId = null;
    this.clearPendingAttachments();
    // Release the rendered messages' attachment object URLs; the singleton cache
    // would otherwise grow for every conversation visited until the component is destroyed.
    this.chatAttachmentService.revokeAll();
    this.streamingStatus = null
    this.isStreaming = false
  }

  @ViewChild('transcript') transcript?: ElementRef<HTMLElement>;
  private turnObserver?: ResizeObserver;
  private scrollFrame?: number;

  ngAfterViewInit(): void {
    this.trySendPendingInitialPrompt();
  }

  async selectConversation() {
    // Report discovery creates/selects the chat before inserting the first message.
    // A parent history refresh must not fetch an empty transcript during that send.
    if (this.messages.length > 0 || this.isStreaming || this.isFindingWebsiteReport) return;
    const id = this.conversation()?.id;
    if (!id || this.internalSelectConversationLoading) return;
    const generation = this.turnPollGeneration;
    this.internalSelectConversationLoading = true
    this.historyPendingReveal = true;
    this.historyLoadError = false;
    const recoverTurn = usesConversationExecution(this.conversation()) && !this.readOnly && !this.isReadOnlySharedConversation;
    const current = () => this.conversation()?.id === id && generation === this.turnPollGeneration;
    try {
      const [, , turn] = await Promise.all([
        this.getConversationMessages(false),
        this.updatePoller?.refresh(true),
        recoverTurn ? lastValueFrom(this.aiService.getActiveConversationTurn(id).pipe(timeout(15000))).catch(() => {
          if (current()) this.toastService.callErrorToast('Could not retrieve the request status. Please try again.');
          return null;
        }) : null,
      ]);
      if (!current()) return;
      this.internalSelectConversationLoading = false;
      if (turn?.turn) this.watchConversationTurn(turn.turn);
      this.conversationLoaded.emit({conversationId: id, success: true});
      this.presentLoadedHistory();
    } catch {
      if (!current()) return;
      // Other parallel reads may still finish after this failure. Do not let
      // them populate a half-loaded chat and prevent the next retry.
      this.turnPollGeneration++;
      this.updatePoller?.stop();
      this.backgroundUpdates = [];
      this.messages = [];
      this.historyConversationId = null;
      this.historyBefore = null;
      this.internalSelectConversationLoading = false;
      this.historyPendingReveal = false;
      this.historyLoadError = true;
      this.toastService.callErrorToast('Could not open this conversation. Please try again.');
      this.conversationLoaded.emit({conversationId: id, success: false});
    }
  }

  private presentLoadedHistory(): void {
    if (!this.historyPendingReveal || this.isConversationLoading || !this.active()) return;
    if (this.historyFrame) cancelAnimationFrame(this.historyFrame);
    const id = this.conversation()?.id;
    this.historyFrame = requestAnimationFrame(() => {
      this.historyFrame = undefined;
      if (this.conversation()?.id !== id || !this.active() || this.isConversationLoading) return;
      const viewport = this.messagesContainer?.nativeElement as HTMLElement | undefined;
      if (!viewport?.getBoundingClientRect().height) return;
      this.historyChangeDetector.detectChanges();
      this.positionReadingTurn();
      this.historyPendingReveal = false;
      this.historyArrived = true;
      this.historyChangeDetector.detectChanges();
    });
  }

  async refreshConversationTurn(): Promise<void> {
    const id = this.conversation()?.id;
    if (!id) return;
    const generation=this.turnPollGeneration;
    this.turnStatusLoading = true;
    try {
      const response = await lastValueFrom(this.aiService.getActiveConversationTurn(id));
      if (this.conversation()?.id === id && generation===this.turnPollGeneration) {
        if(response.turn) this.watchConversationTurn(response.turn);
      }
    } catch {
      this.toastService.callErrorToast('Could not retrieve the request status. Please try again.');
    } finally { this.turnStatusLoading = false; }
  }

  private watchConversationTurn(turn: ConversationTurnStatus, existingMessage?: AiMessage, onComplete?:()=>void, liveDelivery=false): void {
    clearTimeout(this.keywordFollowUpPoll);
    clearTimeout(this.turnPoll);
    const generation=++this.turnPollGeneration;
    const canonicalId=`${turn.turnId}:assistant`;
    const savedMessage=this.messages.find(m=>m.id===canonicalId);
    const message=existingMessage ?? savedMessage ?? [...this.messages].reverse().find(m=>m.role==='assistant'&&m.isStreaming&&m.id.startsWith('temp-assistant-')) ?? {
      id:`${turn.turnId}:assistant`,role:'assistant' as const,content:'',createdAt:turn.createdAt,liveResponse:true,isStreaming:true,
    };
    const pendingUser=this.messages[this.messages.indexOf(message)-1];
    if(pendingUser?.role==='user'&&pendingUser.id.startsWith('temp-')){
      pendingUser.presentationKey??=pendingUser.id;pendingUser.id=`${turn.turnId}:user`;
    }
    message.presentationKey??=message.id;
    message.id=canonicalId;
    this.messages=this.messages.filter(m=>m.id!==canonicalId||m===message);
    if(onComplete)this.onTurnComplete=onComplete;
    if(!this.messages.includes(message))this.messages.push(message);
    const update=async(current:ConversationTurnStatus)=>{
      if(generation!==this.turnPollGeneration||this.conversation()?.id!==turn.conversationId)return;
      const chat = this.conversation();
      if (chat && !this.readOnly) this.novaActivity?.observeTurn(chat, current,
        this.messages.find(message => message.id === `${turn.turnId}:user`)?.content || (pendingUser?.role === 'user' ? pendingUser.content : undefined));
      if(!this.messages.includes(message))this.messages.push(message);
      // Older backends may clear the final failed snapshot. Keep the last displayed text.
      if(current.status!=='failed'||current.content)message.content=current.content??'';
      message.handoffPlan=current.handoffPlan;
      if(current.views){message.views=current.views;this.publishReportCreations(current.views,turn.conversationId);}
      message.activityLabel=current.progress??'Preparing your request';
      this.advanceResponseProgress(message,current.cancellationRequested?'Cancellation requested':message.activityLabel);
      const done=['completed','failed','cancelled'].includes(current.status);
      message.isStreaming=!done;message.responseFailed=current.status==='failed';
      this.isStreaming=!done;
      this.activeConversationTurn=done?null:current;
      if(done){
        this.streamingStatus=null;
        this.aiService.clearRequestIdentity(turn.conversationId);
        if(current.status!=='completed'){
          message.isFinalizing=false;message.isRevealing=false;
          this.onTurnComplete=undefined;
          if(current.status==='cancelled')this.messages=this.messages.filter(m=>m!==message);
          else{message.liveResponse=true;message.responseFailed=true;}
          this.toastService.callInformationToast(current.status==='cancelled'?'Request cancelled.':current.errorCode==='budget_exceeded'?'The request reached its consumption limit. Narrow the request or contact your administrator.':'The request could not be completed. You can send a new request.');
        }else{
          try{await this.getConversationMessages();}catch{/* Keep the saved answer while history is unavailable. */}
          if(this.conversation()?.id===turn.conversationId){const complete=this.onTurnComplete;this.onTurnComplete=undefined;complete?.();}
        }
        this.settleResponseViewport();return;
      }
      this.turnPoll=setTimeout(async()=>{
        try{const result=await lastValueFrom(this.aiService.getConversationTurn(turn.conversationId,turn.turnId));await update(result.turn);}
        catch{
          if(generation!==this.turnPollGeneration)return;
          message.activityLabel='Reconnecting to your saved request';
          this.advanceResponseProgress(message,message.activityLabel);
          this.turnPoll=setTimeout(()=>void update(current),5000);
        }
      },liveDelivery?10000:1500);
    };
    void update(turn);
  }

  async cancelConversationTurn(): Promise<void> {
    const turn = this.activeConversationTurn;
    if (!turn) return;
    this.turnStatusLoading = true;
    try {
      const response = await lastValueFrom(this.aiService.cancelConversationTurn(turn.conversationId, turn.turnId));
      if (this.conversation()?.id !== turn.conversationId) return;
      this.activeConversationTurn = response.turn.status === 'cancelled' ? null : response.turn;
      if (!this.activeConversationTurn) this.aiService.clearRequestIdentity(turn.conversationId);
    } catch {
      this.toastService.callErrorToast('Could not cancel the request. Please try again.');
    } finally { this.turnStatusLoading = false; }
  }

  private setupCopyHandler(): void {
    this.copyHandler = async (event: ClipboardEvent) => {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0 || selection.toString().trim().length === 0) {
        return;
      }

      const range = selection.getRangeAt(0);
      let container: Element | null = range.commonAncestorContainer.nodeType === Node.ELEMENT_NODE
        ? (range.commonAncestorContainer as Element)
        : range.commonAncestorContainer.parentElement;

      while (container && !container.hasAttribute('data-message-id')) {
        container = container.parentElement;
      }

      if (container && container.hasAttribute('data-message-id')) {
        const messageId = container.getAttribute('data-message-id');
        const message = this.messages.find(m => m.id === messageId && m.role === 'assistant');

        if (message) {
          event.preventDefault();
          await this.copySelectedText(range, selection.toString());
        }
      }
    };

    document.addEventListener('copy', this.copyHandler);
  }

  private async copySelectedText(range: Range, plainText: string): Promise<void> {
    try {
      const clonedContents = range.cloneContents();
      const div = document.createElement('div');
      div.appendChild(clonedContents);
      const html = div.innerHTML;

      const blobHtml = new Blob([html], { type: 'text/html' });
      const blobPlain = new Blob([plainText], { type: 'text/plain' });

      await navigator.clipboard.write([
        new ClipboardItem({ 'text/html': blobHtml, 'text/plain': blobPlain }),
      ]);
      this.toastService.callSuccessToast('Message copied!');
    } catch {
      await navigator.clipboard.writeText(plainText);
      this.toastService.callSuccessToast('Message copied!');
    }
  }

  ngOnDestroy(): void {
    this.releaseActivityView?.();
    this.updatePoller?.stop();
    clearTimeout(this.keywordFollowUpPoll);
    clearTimeout(this.turnPoll);
    this.turnPollGeneration++;
    this.turnObserver?.disconnect();
    if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
    if (this.historyFrame) cancelAnimationFrame(this.historyFrame);
    if (this.copyHandler) {
      document.removeEventListener('copy', this.copyHandler);
    }
    this.clearPendingAttachments();
    this.chatAttachmentService.revokeAll();
    this.scribe.reset(this.voiceOwner);
  }

  getRandomSuggestions(array: string[], count: number): string[] {
    const shuffled = [...array];
    const selected: string[] = [];

    for (let i = 0; i < count && i < shuffled.length; i++) {
      const randomIndex = Math.floor(Math.random() * (shuffled.length - i)) + i;
      [shuffled[i], shuffled[randomIndex]] = [shuffled[randomIndex], shuffled[i]];
      selected.push(shuffled[i]);
    }

    return selected;
  }

  onFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.onFilesPicked(input.files);
    input.value = '';
  }

  onDragEnter(event: DragEvent): void {
    if (!this.isFileDrag(event)) return;
    event.preventDefault();
    this.fileDragDepth++;
    this.isDraggingFiles = !this.isAttachmentInputDisabled;
  }

  onDragOver(event: DragEvent): void {
    if (!this.isFileDrag(event)) return;
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = this.isAttachmentInputDisabled ? 'none' : 'copy';
    }
  }

  onDragLeave(event: DragEvent): void {
    if (!this.isFileDrag(event)) return;
    this.fileDragDepth = Math.max(0, this.fileDragDepth - 1);
    if (this.fileDragDepth === 0) this.isDraggingFiles = false;
  }

  onDrop(event: DragEvent): void {
    if (!this.isFileDrag(event)) return;
    event.preventDefault();
    this.fileDragDepth = 0;
    this.isDraggingFiles = false;
    this.onFilesPicked(event.dataTransfer?.files ?? null);
  }

  private isFileDrag(event: DragEvent): boolean {
    return Array.from(event.dataTransfer?.types ?? []).includes('Files');
  }

  onPaste(event: ClipboardEvent): void {
    // Let the textarea's native paste insert text and update the Angular form.
    if (shouldPasteAsText(event.clipboardData)) return;
    const items = event.clipboardData?.items;

    if (!items || this.isAttachmentInputDisabled) return;

    const filesFromClipboard: File[] = [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind === 'file') {
        const file = item.getAsFile();
        if (file) filesFromClipboard.push(file);
      }
    }

    if (!filesFromClipboard.length) return;

    event.preventDefault();
    this.onFilesPicked(filesFromClipboard);
  }

  onFilesPicked(files: FileList | File[] | null): void {
    if (!files || this.isAttachmentInputDisabled) return;

    const selectedFiles = Array.from(files);
    if (!selectedFiles.length) return;

    const availableSlots = this.MAX_ATTACHMENTS_PER_MESSAGE - this.activeAttachmentCount;
    if (availableSlots <= 0) {
      this.toastService.callWarningToast(`You can attach up to ${this.MAX_ATTACHMENTS_PER_MESSAGE} files per message.`);
      return;
    }

    const acceptedFiles: File[] = [];

    for (const file of selectedFiles) {
      const validationMessage = this.validateAttachmentFile(file);
      if (validationMessage) {
        this.toastService.callWarningToast(validationMessage);
        continue;
      }

      if (acceptedFiles.length >= availableSlots) {
        this.toastService.callWarningToast(`You can attach up to ${this.MAX_ATTACHMENTS_PER_MESSAGE} files per message.`);
        break;
      }

      acceptedFiles.push(file);
    }

    acceptedFiles.forEach((file) => {
      const mimeType = resolveAttachmentMimeType(file)!;
      const pendingAttachment: PendingChatAttachment = {
        localId: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        file,
        filename: file.name || 'Attachment',
        mimeType,
        fileSize: file.size,
        status: 'uploading',
        progress: 0,
        previewUrl: mimeType.startsWith('image/') ? window.URL.createObjectURL(file) : undefined,
      };

      this.pendingAttachments.push(pendingAttachment);
      this.uploadPendingAttachment(pendingAttachment);
    });
  }

  retryPendingAttachment(attachment: PendingChatAttachment): void {
    if (attachment.status !== 'error') return;

    if (this.activeAttachmentCount >= this.MAX_ATTACHMENTS_PER_MESSAGE) {
      this.toastService.callWarningToast(`You can attach up to ${this.MAX_ATTACHMENTS_PER_MESSAGE} files per message.`);
      return;
    }

    this.uploadPendingAttachment(attachment);
  }

  removePendingAttachment(attachment: PendingChatAttachment): void {
    attachment.uploadSubscription?.unsubscribe();
    if (attachment.previewUrl) {
      window.URL.revokeObjectURL(attachment.previewUrl);
    }
    this.pendingAttachments = this.pendingAttachments.filter((item) => item.localId !== attachment.localId);
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 B';

    const units = ['B', 'KB', 'MB', 'GB'];
    const unitIndex = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    const size = bytes / Math.pow(1024, unitIndex);

    return `${size.toFixed(size >= 10 || unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
  }

  private validateAttachmentFile(file: File): string | null {
    const mimeType = resolveAttachmentMimeType(file);
    if (!mimeType || !isAllowedAttachmentMimeType(mimeType)) {
      return `${file.name || 'This file'} is not supported. Attach ${this.supportedAttachmentFormats} files.`;
    }

    if (!file.size) {
      return `${file.name || 'This file'} is empty.`;
    }

    if (file.size > this.MAX_ATTACHMENT_SIZE) {
      return `${file.name || 'This file'} is larger than 10 MB.`;
    }

    return null;
  }

  private uploadPendingAttachment(attachment: PendingChatAttachment): void {
    attachment.uploadSubscription?.unsubscribe();
    attachment.status = 'uploading';
    attachment.progress = 0;
    attachment.errorMessage = undefined;

    attachment.uploadSubscription = this.aiService.uploadChatAttachment(attachment.file, this.conversation()?.id).subscribe({
      next: (event) => {
        if (event.type === HttpEventType.UploadProgress) {
          attachment.progress = event.total ? Math.round((event.loaded / event.total) * 100) : 0;
          return;
        }

        if (event.type === HttpEventType.Response && event.body) {
          attachment.id = event.body.id;
          attachment.filename = event.body.filename;
          attachment.mimeType = event.body.mimeType;
          attachment.fileSize = event.body.fileSize;
          attachment.status = 'ready';
          attachment.progress = 100;
        }
      },
      error: () => {
        attachment.status = 'error';
        attachment.progress = 0;
        attachment.errorMessage = 'Upload failed';
        this.toastService.callErrorToast(`Could not upload ${attachment.filename}. Please try again.`);
      },
    });
  }

  private clearPendingAttachments(): void {
    this.pendingAttachments.forEach((attachment) => {
      attachment.uploadSubscription?.unsubscribe();
      if (attachment.previewUrl) {
        window.URL.revokeObjectURL(attachment.previewUrl);
      }
    });
    this.pendingAttachments = [];
  }

  async sendMessage(): Promise<void> {
    if (this.voiceSendPending || this.isResponseActive || this.isConversationLoading) return;
    if (this.voiceInProgress) {
      const generation = this.turnPollGeneration;
      const conversationId = this.conversation()?.id;
      this.voiceSendPending = true;
      try {
        const text = await this.scribe.stopTranscription(this.voiceOwner);
        if (generation !== this.turnPollGeneration || conversationId !== this.conversation()?.id || !this.active() || !this.ownsVoiceInput) return;
        this.messageInput = text;
        if (this.scribe.finalizationIncomplete()) return;
      } finally {
        if (generation === this.turnPollGeneration) this.voiceSendPending = false;
      }
    }
    const content = this.messageInput.trim();
    this.scribe?.reset(this.voiceOwner);
    await this.sendMessageContent(content);
  }

  useSuggestion(suggestion: string): void {
    this.messageInput = suggestion;
    this.sendMessage();
  }

  private responseAnchor?:HTMLElement;
  private revealScrollFrame?:number;
  private scrollToNewResponse(): void {
    if (!this.messagesContainer?.nativeElement) return;
    if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
    this.scrollFrame = requestAnimationFrame(() => {
      const viewport = this.messagesContainer?.nativeElement as HTMLElement;
      const transcript = this.transcript?.nativeElement;
      if (!viewport || !transcript) return;
      this.turnObserver?.disconnect();
      this.responseAnchor=undefined;
      viewport.classList.remove('settling');
      viewport.classList.remove('user-scrolled');
      viewport.style.paddingBottom = '24px';
      this.positionReadingTurn();
      this.turnObserver = new ResizeObserver(()=>this.positionReadingTurn());
      this.turnObserver.observe(transcript);
    });
  }

  private positionReadingTurn(): void {
    const viewport=this.messagesContainer?.nativeElement as HTMLElement|undefined;
    if(!viewport||viewport.classList.contains('user-scrolled'))return;
    const response=viewport.querySelector<HTMLElement>('.latest-response');
    if(!response){viewport.scrollTo({top:viewport.scrollHeight,behavior:'instant'});return;}
    if(this.responseAnchor)return;
    const targetTop = Math.max(0, viewport.scrollTop + response.getBoundingClientRect().top
      - viewport.getBoundingClientRect().top - viewport.clientTop - 12);
    const maximumTop = Math.max(0, viewport.scrollHeight - viewport.clientHeight);
    viewport.scrollTo({ top: Math.min(targetTop, maximumTop), behavior: 'instant' });
    // Follow growth only until the answer's beginning can reach the top.
    // Short answers stop at the natural bottom without artificial blank space.
    if (targetTop <= maximumTop) this.responseAnchor = response;
  }

  onResponseRevealing(message: AiMessage, value: boolean): void {
    if(message.responseFailed){message.isRevealing=false;return;}
    message.isRevealing = value;
    if(value){
      if(this.revealScrollFrame)cancelAnimationFrame(this.revealScrollFrame);
      this.revealScrollFrame=requestAnimationFrame(()=>{
      this.revealScrollFrame=undefined;
      const response=this.messagesContainer?.nativeElement.querySelector('.latest-response');
      if(response)this.turnObserver?.observe(response);
      this.positionReadingTurn();
    });}
    if (!value) this.settleResponseViewport();
  }

  private settleResponseViewport(): void {
    if (this.isStreaming || this.activeConversationTurn || this.messages.at(-1)?.isStreaming || this.messages.at(-1)?.isRevealing) return;
    const viewport = this.messagesContainer?.nativeElement;
    if (viewport) {
      viewport.classList.add('settling');
      viewport.style.paddingBottom = '24px';
      requestAnimationFrame(()=>this.positionReadingTurn());
    }
  }

  async copyResponse(message: string): Promise<void> {
    const cleanMessage = stripCaptureMarker(message);
    try {
      const html = (await Promise.resolve(marked.parse(cleanMessage, { gfm: true, breaks: true }))) as string;
      const blobHtml = new Blob([html], { type: 'text/html' });
      const blobPlain = new Blob([cleanMessage], { type: 'text/plain' });
      await navigator.clipboard.write([
        new ClipboardItem({ 'text/html': blobHtml, 'text/plain': blobPlain }),
      ]);
      this.toastService.callSuccessToast('Message copied!');
    } catch {
      await navigator.clipboard.writeText(cleanMessage);
      this.toastService.callSuccessToast('Message copied!');
    }
  }

  feedbackPending = false;

  async onThumbsUp(message: AiMessage): Promise<void> {
    await this.saveMessageFeedback(message, this.messageHasFeedback(message.id, 'thumbs_up') ? null : 'thumbs_up');
  }

  async openFeedbackModal(message: AiMessage): Promise<void> {
    if (this.feedbackPending) return;
    if (this.messageHasFeedback(message.id, 'thumbs_down')) {
      await this.saveMessageFeedback(message, null);
      return;
    }
    this.selectedMessageForFeedback = message;
    this.selectedFeedbackOption = null;
    this.feedbackObservation = '';
    this.feedbackModalVisible = true;
  }

  closeFeedbackModal(): void {
    if (this.feedbackPending) return;
    this.feedbackModalVisible = false;
    this.selectedMessageForFeedback = null;
    this.selectedFeedbackOption = null;
    this.feedbackObservation = '';
  }

  async onThumbsDown(): Promise<void> {
    if (!this.selectedFeedbackOption || !this.selectedMessageForFeedback) return;
    const label = this.feedbackOptions.find(o => o.value === this.selectedFeedbackOption)?.label ?? this.selectedFeedbackOption;
    if (await this.saveMessageFeedback(this.selectedMessageForFeedback, 'thumbs_down', label, this.feedbackObservation.trim())) {
      this.closeFeedbackModal();
    }
  }

  private async saveMessageFeedback(message: AiMessage, type: 'thumbs_up' | 'thumbs_down' | null, selectedOption = '', observation = ''): Promise<boolean> {
    const conversation = this.conversation();
    if (this.feedbackPending || !conversation || this.readOnly || this.isReadOnlySharedConversation) return false;
    const payload = {
      conversationId: conversation.id, messageId: message.id, type,
      selectedOption: type === 'thumbs_up' ? 'Thumbs up' : selectedOption,
      observation, outputFlagged: message.content ?? '',
      orgId: this.authService.getCurrentOrganization() ?? undefined,
    };
    this.feedbackPending = true;
    try {
      const result = await lastValueFrom(this.aiService.sendChatbotFeedback(payload));
      // A user may switch chats while this request is pending.
      if (this.conversation()?.id === conversation.id) {
        const updated = { ...this.conversation()!, attributes: {
          ...this.conversation()!.attributes,
          positiveFeedbackMessages: result.conversation.attributes?.positiveFeedbackMessages ?? [],
          negativeFeedbackMessages: result.conversation.attributes?.negativeFeedbackMessages ?? [],
        } };
        this.skipNextEffect = true;
        this.conversation.set(updated);
        this.setConversation.emit(updated);
      }
      if (type) this.posthogService.track(type === 'thumbs_up' ? 'chatbot_positive_feedback_sent' : 'chatbot_negative_feedback_sent', { message: message.content });
      this.toastService.callSuccessToast(type ? 'Your feedback on the message was saved successfully!' : 'Your feedback on the message was removed successfully!');
      return true;
    } catch {
      this.toastService.callErrorToast('Failed to save feedback. Please try again.');
      return false;
    } finally {
      this.feedbackPending = false;
    }
  }

  async getConversationMessages(refreshUpdates = true) {
    const id = this.conversation()?.id as string;
    const generation=this.turnPollGeneration;
    const res: GetMessagesResponse = await lastValueFrom(
      (this.readOnly
        ? this.aiService.getAdminConversationMessages(id)
        : this.aiService.getChatbotMessages(id)).pipe(timeout(15000))
    )
    if(this.conversation()?.id!==id||generation!==this.turnPollGeneration)return;
    const same=this.historyConversationId===id;
    const earliest=res.messages[0]?.createdAt;
    const older=same&&res.hasMore&&earliest?this.messages.filter(m=>m.createdAt<earliest&&!res.messages.some(n=>n.id===m.id)):[];
    this.messages = mergeConversationUpdates(
      this.sanitizeScanMessages(this.preserveResponsePresentation([...older,...res.messages])),
      this.backgroundUpdates ?? [],
    );
    if(!same||!older.length)this.historyBefore=res.hasMore?res.nextBefore??null:null;
    this.historyConversationId=id;
    this.watchKeywordFollowUps(id);
    if (refreshUpdates) void this.updatePoller?.refresh();
  }

  /** Delivery continues after the creation turn has finished, and resumes on reload. */
  private watchKeywordFollowUps(id:string):void {
    clearTimeout(this.keywordFollowUpPoll);
    const pending=hasPendingKeywordSuggestions(this.messages);
    if(!pending)return;
    this.keywordFollowUpPoll=setTimeout(async()=>{
      if(this.conversation()?.id!==id)return;
      if(this.isStreaming||this.activeConversationTurn){this.watchKeywordFollowUps(id);return;}
      try{await this.getConversationMessages();}
      catch{if(this.conversation()?.id===id)this.watchKeywordFollowUps(id);}
    },5000);
  }

  async loadEarlierMessages():Promise<void>{
    const id=this.conversation()?.id,before=this.historyBefore,generation=this.turnPollGeneration;
    if(!id||before===null||this.loadingEarlierMessages||this.readOnly)return;
    const viewport=this.messagesContainer?.nativeElement as HTMLElement|undefined;
    viewport?.classList.add('user-scrolled');
    this.loadingEarlierMessages=true;
    try{
      const page=await lastValueFrom(this.aiService.getChatbotMessages(id,before));
      if(this.conversation()?.id!==id||generation!==this.turnPollGeneration)return;
      const height=viewport?.scrollHeight??0,top=viewport?.scrollTop??0;
      const bounds=viewport?.getBoundingClientRect();
      const anchor=viewport&&bounds?Array.from(viewport.querySelectorAll<HTMLElement>('.user-turn,.assistant-turn')).find(node=>node.getBoundingClientRect().bottom>bounds.top):undefined;
      const anchorTop=anchor?.getBoundingClientRect().top;
      const ids=new Set(this.messages.map(m=>m.id));
      this.messages=[...this.sanitizeScanMessages(page.messages.filter(m=>!ids.has(m.id))),...this.messages];
      this.historyBefore=page.hasMore?page.nextBefore??null:null;
      // Render the prepend before measuring: a frame can precede Angular's queued update.
      this.historyChangeDetector.detectChanges();
      const restore=()=>{
        if(!viewport||this.conversation()?.id!==id||generation!==this.turnPollGeneration)return;
        viewport.scrollTop=anchor?.isConnected&&anchorTop!==undefined
          ?viewport.scrollTop+anchor.getBoundingClientRect().top-anchorTop:top+viewport.scrollHeight-height;
      };
      restore();requestAnimationFrame(restore);
    }catch{if(this.conversation()?.id===id)this.toastService.callInformationToast('Earlier messages could not be loaded. Your conversation is saved; try again.');}
    finally{if(generation===this.turnPollGeneration)this.loadingEarlierMessages=false;}
  }

  private preserveResponsePresentation(saved:AiMessage[]):AiMessage[]{
    const current=new Map(this.messages.map(message=>[message.id,message]));
    return saved.map(message=>{
      const live=current.get(message.id);
      if(!live)return message;
      if(!live.liveResponse)return Object.assign(live,message,{presentationKey:live.presentationKey});
      const presentation={liveResponse:true,presentationKey:live.presentationKey,revealState:live.revealState,
        isStreaming:live.isStreaming,isFinalizing:live.isFinalizing,isRevealing:live.isRevealing,
        progressSteps:live.progressSteps,views:message.views??live.views};
      return Object.assign(live,message,presentation);
    });
  }

  /**
   * Scan-analysis conversations open with a system-crafted seed turn + the raw JSON
   * summary. Hide those so the thread reads cleanly; if nothing else is there yet,
   * show a friendly opener instead of an empty chat. No-op for normal chats.
   */
  private sanitizeScanMessages(messages: AiMessage[]): AiMessage[] {
    const isArtifact = (m: AiMessage): boolean => {
      // The backend prepends the page-context block to every sent turn, the seeded
      // scan-analysis turn included — strip it before matching on the lead-in.
      const c = stripPageContext((m.content ?? '').trim()).trim();
      if (c.startsWith('A single-page website compliance scan just finished')) return true;
      if (
        m.role === 'assistant' &&
        c.startsWith('{') &&
        c.includes('"summary"') &&
        c.includes('"relativeRisk"')
      ) {
        return true;
      }
      return false;
    };
    const wasScanChat = messages.some(isArtifact);
    const visible = messages.filter((m) => !isArtifact(m));
    if (wasScanChat && !visible.some((m) => m.role === 'assistant')) {
      visible.unshift({
        id: 'scan-analysis-intro',
        role: 'assistant',
        content:
          "I've reviewed your scan and prepared the summary on the report. Answer my questions there, or ask me anything about the findings — I can rule out false positives and refine the analysis.",
        createdAt: new Date().toISOString(),
      } as AiMessage);
    }
    return visible;
  }

  async createConversation() {
    let conversationName = this.datePipe.transform(new Date().toString(), 'short') ?? ''
    if(this.assistant) conversationName = 'Assistant ' + conversationName;
    const res = await lastValueFrom(this.aiService.createChatbotConversation(conversationName, this.attributes))
    this.skipNextEffect = true;
    this.setConversation.emit(res.conversation)
    this.conversation.update(() => res.conversation)
    this.getConversations.emit()
  }

  private async generateConversationTitle(conversationId: string, content: string): Promise<void> {
    try {
      const res = await lastValueFrom(this.aiService.generateConversationTitle(conversationId, this.conversation()?.attributes as Record<string, string>, content))
      const generatedName = res?.name

      // Bail if the title came back empty or the user already switched to another conversation.
      if (!generatedName || this.conversation()?.id !== conversationId) return;

      // The returned name is authoritative for display and the backend persists it
      // asynchronously, so apply it locally rather than refetching — a refetch could race the
      // write and momentarily show the placeholder name again.
      this.skipNextEffect = true;
      this.conversation.update(current => current ? { ...current, name: generatedName } : current)
      this.setConversation.emit(this.conversation())
    } catch (error) {
      // Non-200 (e.g. the LLM is unavailable) — keep the placeholder name, no retry needed.
      console.error('[MESSAGES_CONTAINER] Failed to generate conversation title:', error);
    }
  }

  private shouldAutoSendPendingPrompt(): boolean {
    const prompt = this.pendingInitialPrompt()?.trim();
    return !!this.assistant && !!prompt && !this.messages.length && !this.isStreaming;
  }

  private async trySendPendingInitialPrompt(): Promise<void> {
    const conversationId = this.conversation()?.id;
    const prompt = this.pendingInitialPrompt()?.trim();

    if (!conversationId || !prompt || !this.shouldAutoSendPendingPrompt()) return;

    const pendingPromptKey = `${conversationId}:${prompt}`;
    if (this.consumedPendingPromptKey === pendingPromptKey) return;

    this.consumedPendingPromptKey = pendingPromptKey;
    await this.sendMessageContent(prompt);
  }

  websiteScanList(message: AiMessage): WebsiteScanList | null {
    if(!this.websiteReviewPresentationEnabled || this.assistant || message.role!=='user') return null;
    const list=parseWebsiteScanList(stripPageContext(message.content))?.list;
    return list?.organizationId===this.authService.getCurrentOrganization()
      && list.userId===this.authService.getCurrentUser()?.uid ? list : null;
  }

  private precedingUserMessage(index: number): AiMessage | undefined {
    return this.messages.slice(0, index).reverse().find(message => message.role === 'user');
  }

  private hasWebsiteContext(): boolean {
    const type = this.conversation()?.attributes?.reportType;
    if (type) return type === 'website' || type === 'website-legacy';
    const previous = this.precedingUserMessage(this.messages.length);
    return !!previous && !!this.websiteScanList(previous);
  }

  responseRequest(index: number): string {
    const request = this.precedingUserMessage(index);
    return request ? parseMessageContent(request.content).text : '';
  }

  websiteScanListForReply(index: number): WebsiteScanList | null {
    if (this.messages[index]?.role !== 'assistant') return null;
    const request = this.precedingUserMessage(index);
    return request ? this.websiteScanList(request) : null;
  }

  websiteReportPresentation(index: number): { requested: boolean; name: string; scanDate: string; path: string; summaryOnly: boolean } {
    const request = this.precedingUserMessage(index);
    const text = request ? parseMessageContent(request.content).text : '';
    const named = text.match(/^Review the website report ("(?:[^"\\]|\\.)*")\./);
    const legacy = request ? this.websiteReportSummary(request) : null;
    const reference = request ? parseWebsiteReportReference(request.content)?.reference : undefined;
    const historical = request ? historicalWebsiteReportId(request.content) : null;
    const identity = reference ?? (legacy ? {id:legacy.projectId, legacy:true} : historical);
    const listed = identity ? this.messages.flatMap(message => this.websiteScanList(message)?.items ?? []).find(item => item.id === identity.id) : undefined;
    const attributes = this.conversation()?.attributes;
    const matchingCurrent = identity?.id && attributes?.projectId === identity.id && attributes?.novaReportSelectionId === identity.id;
    let name = reference?.name ?? legacy?.name ?? listed?.name ?? (matchingCurrent ? attributes.novaReportName : '') ?? '';
    if (named && !name) {
      try { name = JSON.parse(named[1]); } catch { /* Free-text prompts may contain invalid JSON escapes. */ }
    }
    if (!name && identity) name = `Report ${identity.id}`;
    return {
      requested: !!named || /^Review the selected website report\./.test(text)
        || requestsLatestWebsiteReport(text, !!identity) || requestsWebsiteReportDiscussion(text, !!identity),
      name,
      scanDate: reference?.scanDate ?? listed?.scanDate ?? (matchingCurrent ? attributes.novaReportScanDate : '') ?? '',
      path: identity ? reportReferencePath(identity) : '',
      summaryOnly: identity?.legacy ?? false,
    };
  }

  websiteReportSummary(message: AiMessage) {
    return this.websiteReviewPresentationEnabled && !this.assistant && message.role==='user'
      ? parseWebsiteReportSummary(stripPageContext(message.content))?.summary : null;
  }

  isActiveWebsiteScan(scanId: string): boolean {
    const attributes = this.conversation()?.attributes;
    return attributes?.projectId === scanId
      && (attributes.reportType === 'website' || attributes.reportType === 'website-legacy');
  }

  private assertReportDiscoveryContext(conversationId: string | undefined, userId: string, organizationId: string) {
    if(this.conversation()?.id!==conversationId || this.authService.getCurrentUser()?.uid!==userId
      || this.authService.getCurrentOrganization()!==organizationId) {
      throw new ReportDiscoveryError('The conversation or organization changed. Please try again.');
    }
  }

  private async applyReportSelection(project: ReportCandidate, conversationId: string | undefined, userId: string, organizationId: string, legacy = false) {
    this.assertReportDiscoveryContext(conversationId,userId,organizationId);
    if(!conversationId) await this.createConversation();
    const current=this.conversation();
    if(!current?.id || current.attributes?.userId!==userId || current.attributes?.organizationId!==organizationId) {
      throw new ReportDiscoveryError('This conversation does not belong to the current account and organization.');
    }
    const updated=await lastValueFrom(this.aiService.editConversation(current.id, {
      name:current.name, attributes:{...current.attributes,projectId:project.id,reportType:project.type==='documents'?'documents':legacy?'website-legacy':'website',
        novaReportSelectionId:project.id,novaReportName:project.name,novaReportScanDate:new Date(project.createdAt).toISOString()},
    }));
    this.assertReportDiscoveryContext(current.id,userId,organizationId);
    if(updated.conversation.attributes?.projectId!==project.id) throw new ReportDiscoveryError('Could not select the report.');
    this.skipNextEffect=true;this.conversation.set(updated.conversation);this.setConversation.emit(updated.conversation);
  }

  async reviewWebsiteScan(item: WebsiteScanItem): Promise<void> {
    if(!this.websiteReviewPresentationEnabled||this.assistant||this.readOnly||this.isReadOnlySharedConversation
      ||this.isStreaming||this.isConversationLoading||this.messageInput.trim()||this.pendingAttachments.length) return;
    const userId=this.authService.getCurrentUser()?.uid, organizationId=this.authService.getCurrentOrganization();
    if(!userId||!organizationId) return;
    const conversationId=this.conversation()?.id;
    this.isStreaming=true;this.isFindingWebsiteReport=true;this.streamingStatus='Opening the selected website report…';
    this.reportDiscoveryError=null;
    let selected=false;
    let prompt='';
    try {
      const result=await this.latestWebsiteReportService.readSelection(item,userId,organizationId);
      await this.applyReportSelection(result.project,conversationId,userId,organizationId,!!result.summary);
      const question=`Review the website report ${JSON.stringify(result.project.name || 'Untitled website report')}. Give the main findings and one useful next step concisely. Start directly with the findings, without an introductory sentence. Do not change any findings or scan data.`;
      prompt=question;
      if(result.summary) prompt=appendWebsiteReportSummary(question,result.summary);
      selected=true;
    } catch {
      this.reportDiscoveryError='Could not open a completed report for this scan. It may still be running or access may have changed. Refresh the list or open the report to check.';
    } finally {
      this.isStreaming=false;this.isFindingWebsiteReport=false;this.streamingStatus=null;
    }
    if(selected) await this.sendMessageContent(prompt);
  }

  private async sendMessageContent(content: string): Promise<void> {
    const trimmedContent = content.trim();
    const readyAttachments = this.readyAttachments;
    const attachmentIds = readyAttachments
      .map((attachment) => attachment.id)
      .filter((id): id is string => !!id);
    const attachmentMarkers = buildMarkers(
      readyAttachments.map((attachment) => ({
        id: attachment.id as string,
        filename: attachment.filename,
      }))
    );
    let wireContent = [trimmedContent, attachmentMarkers].filter(Boolean).join('\n\n');
    const persistedMessageCountBeforeSend = this.messages.length;
    // One fallback for every empty response now.
    //
    // A request to remove false positives used to be special-cased into "this type of
    // interaction is not fully supported yet" - a placeholder that fired on a keyword
    // match against the user's own words, whether or not that was actually what went
    // wrong. It hid genuine errors behind a roadmap message, and it is untrue as of M2:
    // the flow it apologised for is now built. Removing it means a real failure reads as
    // a real failure (FALSE_POSITIVE_REVIEW_DESIGN.md, M2 scope).
    const emptyResponseFallbackMessage = this.GENERIC_FALLBACK_MESSAGE;

    if (
      (!trimmedContent && !readyAttachments.length)
      || this.readOnly
      || this.isStreaming
      || !!this.messages.at(-1)?.isRevealing
      || !!this.activeConversationTurn
      || this.isConversationLoading
      || this.isReadOnlySharedConversation
      || trimmedContent.length > this.MAX_MESSAGE_LENGTH
      || this.hasUploadingAttachments
      || this.hasAttachmentErrors
    ) {
      return;
    }

    if (!this.assistant && !this.messages.length && this.welcomeOrb && this.welcomePanel) {
      this.welcomeDeparture.emit({ orb: this.welcomeOrb.nativeElement, welcome: this.welcomePanel.nativeElement });
    }
    this.suggestionUsed.emit();
    // Invalidate history/recovery reads started before this optimistic send.
    ++this.turnPollGeneration;
    this.reportDiscoveryError = null;
    const startedWithoutConversation = !this.conversation()?.id;
    // Native tools resolve platform intent on the server; forward the user wording unchanged.

    const userMessage: AiMessage = {
      id: `temp-${Date.now()}`,
      role: 'user',
      content: wireContent,
      createdAt: new Date().toISOString(),
    };

    this.messages.push(userMessage);
    // Keep uploaded files/previews until acceptance, so a rejection can restore the draft.
    this.pendingAttachments = [];
    this.messageInput = '';

    this.scrollToNewResponse()

    const assistantMessage: AiMessage = {
      id: `temp-assistant-${Date.now()}`,
      role: 'assistant',
      content: '',
      createdAt: new Date().toISOString(),
      isStreaming: true,
      liveResponse: true,
      progressSteps: [progressLabels.thinking],
    };
    this.messages.push(assistantMessage);
    this.scrollToNewResponse()

    const requestId = crypto.randomUUID();
    const turnStartedAt = performance.now();
    const logTurnTiming = (phase: string, extra: Record<string, unknown> = {}) => console.info(JSON.stringify({
      event: 'novaai_chat_timing', source: 'browser', requestId, phase,
      elapsedMs: Math.round(performance.now() - turnStartedAt), ...extra,
    }));
    logTurnTiming('turn_started');
    let firstAssistantDeltaLogged = false;
    let admittedTurn = false;
    let draftRestored = false;
    let sentConversationId = this.conversation()?.id;
    const releaseAttachments = () => {
      readyAttachments.forEach(attachment => {
        if (attachment.previewUrl) window.URL.revokeObjectURL(attachment.previewUrl);
      });
    };
    const restoreDraft = () => {
      if (draftRestored || admittedTurn || firstAssistantDeltaLogged) return;
      draftRestored = true;
      if (this.conversation()?.id !== sentConversationId) { releaseAttachments(); return; }
      // Do not overwrite a newer draft typed while the request was in flight.
      if (!this.messageInput && !this.pendingAttachments.length) {
        this.messageInput = content;
        this.pendingAttachments = readyAttachments;
        // This user message was never accepted; the rejected assistant shell still
        // carries the visible error explaining why the draft returned to the composer.
        this.messages = this.messages.filter(message => message !== userMessage);
      } else { releaseAttachments(); }
    };

    try {
      this.isStreaming = true;
      const isNewConversation = startedWithoutConversation;
      if (!this.conversation()?.id) {
        await this.createConversation();
        sentConversationId = this.conversation()?.id;
        logTurnTiming('conversation_created');
      }

      this.posthogService.track('chatbot_message_sent', {
        message: trimmedContent,
      });

      const conversationId = this.conversation()?.id!;
      let responseFailed = false;

      this.aiService
        .sendMessageStream(conversationId, { content: wireContent, attachmentIds, clientRequestId: requestId })
        .subscribe({
          next: (chunk: ConversationStreamEvent) => {
            if (assistantMessage.responseFailed) return;
            if (this.conversation()?.id !== conversationId) return;
            if (chunk.event === 'turn-status') {
              admittedTurn = true;
              releaseAttachments();
              const turn = JSON.parse(chunk.data) as ConversationTurnStatus & {liveDelivery?:boolean};
              if (this.conversation()?.id === sentConversationId) {
                this.activeConversationTurn = ['completed', 'cancelled', 'failed'].includes(turn.status) ? null : turn;
                this.watchConversationTurn(turn,assistantMessage,()=>{if(isNewConversation)void this.generateConversationTitle(conversationId,trimmedContent);},turn.liveDelivery===true);
                if (turn.status === 'cancelled' || turn.status === 'failed') {
                  this.toastService.callInformationToast('The previous request ended without an answer. You can send a new request.');
                }
              }
              return;
            }
            if (chunk.event === 'response-acknowledgement' && !assistantMessage.activityLabel && !assistantMessage.content) {
              try {
                const value = JSON.parse(chunk.data);
                if (value.version === 1 && typeof value.text === 'string' && value.text.length <= 180) assistantMessage.acknowledgement = value.text;
              } catch { /* Optional acknowledgement never interrupts the answer. */ }
            }
            const progress = readResponseProgress(chunk.event, chunk.data);
            if (progress) {
              assistantMessage.deferPresentation ||= progress.deferred;
              if (progress.label) assistantMessage.activityLabel = progress.label;
              this.advanceResponseProgress(assistantMessage, progress.label || assistantMessage.activityLabel || progressLabels[progress.phase]);
              if (this.activeConversationTurn) this.novaActivity?.updateTaskProgress(conversationId, this.activeConversationTurn.turnId,
                progress.label || progressLabels[progress.phase]);
            }
            if (chunk.event === 'response-view') {
              try {
                const payload = JSON.parse(chunk.data);
                const views = payload.version === 1 ? nativeResponseViews(payload.views) : [];
                if (views.length) { assistantMessage.views = views; assistantMessage.deferPresentation = true;this.publishReportCreations(views,conversationId); }
              } catch { /* Unknown templates keep the ordinary answer path. */ }
            }
            if (chunk.event === 'assistant-delta') {
              if (!assistantMessage.content && !assistantMessage.activityLabel) this.advanceResponseProgress(assistantMessage, progressLabels.composing);
              if (!firstAssistantDeltaLogged) { releaseAttachments(); firstAssistantDeltaLogged = true; }
              assistantMessage.content += chunk.data;
            } else if (chunk.event === 'error') {
              responseFailed = true;
              assistantMessage.responseFailed = true;
              restoreDraft();
              logTurnTiming('stream_error_event');
              this.isStreaming = false;
              this.streamingStatus = null;
              if (!this.websiteReviewPresentationEnabled) assistantMessage.content ||= emptyResponseFallbackMessage;
              assistantMessage.isStreaming = false;
              assistantMessage.isFinalizing = false;
              assistantMessage.isRevealing = false;
              this.settleResponseViewport();
              return;
            }
          },
          error: (error) => {
            if (this.conversation()?.id !== conversationId) return;
            if(admittedTurn){if(this.activeConversationTurn)this.watchConversationTurn(this.activeConversationTurn,assistantMessage);return;}
            restoreDraft();
            logTurnTiming('stream_failed', { errorType: error instanceof Error ? error.name : 'unknown' });
            console.error(error);
            this.isStreaming = false;
            this.streamingStatus = null
            assistantMessage.responseFailed = true;
            if (!firstAssistantDeltaLogged) assistantMessage.sendErrorMessage = conversationSendErrorMessage(error);
            if (!this.websiteReviewPresentationEnabled) assistantMessage.content ||= this.GENERIC_FALLBACK_MESSAGE;
            assistantMessage.isStreaming = false;
            assistantMessage.isFinalizing = false;
            assistantMessage.isRevealing = false;
            this.settleResponseViewport();
          },
          complete: async () => {
            if (assistantMessage.responseFailed) return;
            if (this.conversation()?.id !== conversationId) return;
            // HTTP admission is finished; the durable request can still be running.
            if (admittedTurn) { if(this.activeConversationTurn)this.watchConversationTurn(this.activeConversationTurn,assistantMessage);this.streamingStatus = null; this.settleResponseViewport(); return; }
            if (!firstAssistantDeltaLogged) restoreDraft();
            // Keep this turn exclusive until history reconciliation finishes.
            this.isStreaming = true;
            this.streamingStatus = null
            if (!this.websiteReviewPresentationEnabled) assistantMessage.content ||= emptyResponseFallbackMessage;
            assistantMessage.isStreaming = true;
            assistantMessage.isFinalizing = true;
            this.advanceResponseProgress(assistantMessage, 'Finishing your response');
            try { await this.handleAssistantStreamComplete(persistedMessageCountBeforeSend + 2); }
            catch { /* Keep the streamed reply if history is temporarily unavailable. */ }
            finally {
              assistantMessage.isStreaming = false;
              assistantMessage.isFinalizing = false;
              if (this.conversation()?.id === conversationId) {
                this.isStreaming = false;
                const finalMessage = this.messages.at(-1);
                if (finalMessage?.role === 'assistant') { finalMessage.isStreaming = false; finalMessage.isFinalizing = false; }
                this.settleResponseViewport();
              }
            }
            // Title generation can compete for the provider's conversation lock. Start it
            // after native report selection too, so neither update overwrites the other.
            if (isNewConversation && !responseFailed && this.conversation()?.id === conversationId) {
              void this.generateConversationTitle(conversationId, trimmedContent);
            }
          },
        });
    } catch (error) {
      restoreDraft();
      logTurnTiming('turn_failed', { errorType: error instanceof Error ? error.name : 'unknown' });
      this.isStreaming = false;
      this.streamingStatus = null;
      assistantMessage.responseFailed = true;
      if (!firstAssistantDeltaLogged) assistantMessage.sendErrorMessage = conversationSendErrorMessage(error);
      if (!this.websiteReviewPresentationEnabled) assistantMessage.content ||= this.GENERIC_FALLBACK_MESSAGE;
      assistantMessage.isStreaming = false;
      assistantMessage.isFinalizing = false;
      assistantMessage.isRevealing = false;
    }
  }

  async delay(ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // The backend can persist the final user/assistant pair slightly after the stream completes,
  // so retry briefly before replacing local optimistic messages with the server history. If the
  // expected pair never shows up (the assistant turn failed to persist upstream), keep the local
  // optimistic messages instead of overwriting them with a server history that's missing the
  // assistant's reply — otherwise the message the user already saw would just vanish.
  private async syncConversationMessagesAfterStream(expectedMinimumCount: number): Promise<void> {
    const maxAttempts = 6;
    const conversationId = this.conversation()?.id;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const res: GetMessagesResponse = await lastValueFrom(this.aiService.getChatbotMessages(conversationId as string).pipe(timeout(10000)));
      if (this.conversation()?.id !== conversationId) return;

      if (res.messages.length >= expectedMinimumCount) {
        const pending = this.messages.at(-1);
        const finalMessage = res.messages.at(-1);
        const pendingUser=this.messages.at(-2),savedUser=res.messages.at(-2);
        if(pendingUser?.role==='user'&&savedUser?.role==='user'&&pendingUser.content===savedUser.content)savedUser.presentationKey=pendingUser.presentationKey??pendingUser.id;
        if (pending?.liveResponse && finalMessage?.role === 'assistant') {
          Object.assign(finalMessage, { liveResponse: true, presentationKey:pending.presentationKey??pending.id,revealState:pending.revealState,deferPresentation: pending.deferPresentation,
            isStreaming: pending.isStreaming, isFinalizing: pending.isFinalizing, isRevealing: pending.isRevealing, progressSteps: pending.progressSteps, activityLabel: pending.activityLabel, acknowledgement: pending.acknowledgement,
            views: finalMessage.views ?? pending.views });
        }
        this.messages = this.sanitizeScanMessages(res.messages);
        const views = nativeResponseViews(this.messages.at(-1)?.views);
        // A single reviewed report becomes the follow-up context. Comparisons do not pick one arbitrarily.
        if (!this.assistant && views.length === 1 && (views[0].template === 'website-report' || views[0].template === 'document-report')) {
          const report = views[0].report;
          const userId = this.authService.getCurrentUser()?.uid;
          const organizationId = this.authService.getCurrentOrganization();
          if (userId && organizationId && this.conversation()?.attributes?.projectId !== report.id) {
            try { await this.applyReportSelection({ id: report.id, name: report.name, createdAt: report.scanDate,
              type: views[0].template==='document-report'?'documents':'website', userId, shared: false }, conversationId, userId, organizationId, report.path.includes('/legacy/')); }
            catch { this.reportDiscoveryError = 'The report was read, but could not be selected for follow-up questions. Mention its name when continuing.'; }
          }
        }
        return;
      }

      if (attempt === maxAttempts - 1) return;

      await this.delay(500);
    }
  }

  private async handleAssistantStreamComplete(expectedMinimumCount: number): Promise<void> {
    // Page invalidation is independent of presenting the answer; do not hold the card for it.
    void this.loadLatestAssistantTools();
    await this.syncConversationMessagesAfterStream(expectedMinimumCount);
  }

  private publishReportCreations(views:unknown,conversationId:string):void{
    const userId=this.authService.getCurrentUser()?.uid,organizationId=this.authService.getCurrentOrganization();
    if(this.readOnly||this.isReadOnlySharedConversation||!userId||!organizationId||this.conversation()?.id!==conversationId)return;
    this.aiToolEventsService.emitReportCreations(views,{userId,organizationId,conversationId});
  }

  private async loadLatestAssistantTools(): Promise<void> {
    const conversationId = this.conversation()?.id;

    if (!conversationId) {
      return;
    }

    try {
      const response = await lastValueFrom(this.aiService.getLatestAssistantTools(conversationId).pipe(timeout(10000)));
      this.aiToolEventsService.emitTools(response.tools);
    } catch (error) {
      console.error('[MESSAGES_CONTAINER] Failed to load latest assistant tools:', error);
    }
  }

  private advanceResponseProgress(message: AiMessage, label: string): void {
    const steps = message.progressSteps ?? [];
    if (steps.at(-1) !== label) message.progressSteps = [...steps.slice(-7), label];
  }

  hasStreamingAssistantWaitingFirstChar() {
    return this.messages.some((message) => message.role === 'assistant' && message.isStreaming && !message.content);
  }

  messageHasFeedback(id: string, type: 'thumbs_down' | 'thumbs_up') {
    if(type === 'thumbs_up' && this.conversation()?.attributes?.positiveFeedbackMessages && this.conversation()?.attributes?.positiveFeedbackMessages?.includes(id)) return true
    if(type === 'thumbs_down' &&this.conversation()?.attributes?.negativeFeedbackMessages && this.conversation()?.attributes?.negativeFeedbackMessages?.includes(id)) return true
    return false
  }
}
